import React, { useEffect, useRef, useState } from "react";
import { Typography, Space, Avatar, Tooltip, Spin, Empty, Divider, Button } from "antd";
import { UserOutlined, CheckOutlined, CheckCircleOutlined, PaperClipOutlined, DownOutlined, UpOutlined } from "@ant-design/icons";
import { format, isToday, isYesterday, isSameDay } from "date-fns";
import { de } from "date-fns/locale";
import { Thread } from "./ConversationList";

const { Text, Title, Paragraph } = Typography;

interface Message {
  id: number;
  guest_uid: number;
  owner_uid: number;
  subject: string;
  body: string;
  body_preview: string;
  direction: "incoming" | "outgoing";
  email_address_from: string;
  email_address_to: string;
  message_id: string;
  in_reply_to: string;
  received_at: number;
  is_read: boolean;
  attachments: any[];
}

interface ConversationViewProps {
  messages: Message[];
  loading: boolean;
  guestInfo?: Thread;
}

export const ConversationView: React.FC<ConversationViewProps> = ({ messages, loading, guestInfo }) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [expandedMessages, setExpandedMessages] = useState<Set<number>>(new Set());

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const formatMessageTime = (timestamp: number) => {
    const date = new Date(timestamp * 1000);
    if (isToday(date)) {
      return format(date, "HH:mm", { locale: de });
    } else if (isYesterday(date)) {
      return `Gestern ${format(date, "HH:mm", { locale: de })}`;
    } else {
      return format(date, "dd.MM.yyyy HH:mm", { locale: de });
    }
  };

  const formatDateSeparator = (timestamp: number) => {
    const date = new Date(timestamp * 1000);
    if (isToday(date)) {
      return "Heute";
    } else if (isYesterday(date)) {
      return "Gestern";
    } else {
      return format(date, "EEEE, d. MMMM yyyy", { locale: de });
    }
  };

  const shouldShowDateSeparator = (currentMessage: Message, previousMessage?: Message) => {
    if (!previousMessage) return true;
    const currentDate = new Date(currentMessage.received_at * 1000);
    const previousDate = new Date(previousMessage.received_at * 1000);
    return !isSameDay(currentDate, previousDate);
  };

  const toggleMessageExpansion = (messageId: number) => {
    const newExpanded = new Set(expandedMessages);
    if (newExpanded.has(messageId)) {
      newExpanded.delete(messageId);
    } else {
      newExpanded.add(messageId);
    }
    setExpandedMessages(newExpanded);
  };

  const getGuestDisplayName = () => {
    if (guestInfo?.guest_info) {
      const { lastname, firstname } = guestInfo.guest_info;
      if (lastname || firstname) {
        return `${lastname}${lastname && firstname ? ', ' : ''}${firstname}`.trim();
      }
      return guestInfo.guest_info.email || "Unbekannter Gast";
    }
    // Fallback to last message email
    if (guestInfo?.lastMessage?.email_from) {
      const email = guestInfo.lastMessage.email_from;
      const name = email.split("@")[0];
      return name.charAt(0).toUpperCase() + name.slice(1);
    }
    return "Unbekannter Gast";
  };

  const getGuestEmail = () => {
    if (guestInfo?.guest_info?.email) {
      return guestInfo.guest_info.email;
    }
    return guestInfo?.lastMessage?.email_from || "";
  };

  const getInitials = () => {
    if (guestInfo?.guest_info) {
      const { firstname, lastname } = guestInfo.guest_info;
      if (firstname && lastname) {
        return firstname.charAt(0).toUpperCase() + lastname.charAt(0).toUpperCase();
      }
      if (lastname) {
        return lastname.substring(0, 2).toUpperCase();
      }
      if (firstname) {
        return firstname.substring(0, 2).toUpperCase();
      }
    }
    return "?";
  };

  if (loading) {
    return (
      <div className="conversation-loading">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="conversation-view">
      {guestInfo && (
        <div className="conversation-header">
          <Space align="center" size={16}>
            <Avatar size={48} icon={<UserOutlined />}>
              {getInitials()}
            </Avatar>
            <div>
              <Title level={5} style={{ margin: 0 }}>
                {getGuestDisplayName()}
              </Title>
              <Text type="secondary">{getGuestEmail()}</Text>
            </div>
          </Space>
        </div>
      )}

      <div className="messages-container">
        {messages.length === 0 ? (
          <div className="conversation-empty">
            <Empty 
              description="Noch keine Nachrichten" 
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
            <Text className="start-chat-hint">
              Beginnen Sie die Konversation, indem Sie unten eine Nachricht eingeben.
            </Text>
          </div>
        ) : (
          messages.map((message, index) => {
            const showDateSeparator = shouldShowDateSeparator(message, messages[index - 1]);
            const isExpanded = expandedMessages.has(message.id);
            const isLongMessage = message.body.length > 300;

            return (
              <React.Fragment key={message.id}>
                {showDateSeparator && (
                  <div className="date-separator">
                    <Divider>{formatDateSeparator(message.received_at)}</Divider>
                  </div>
                )}

                <div className={`message-wrapper ${message.direction}`}>
                  <div className={`message-bubble ${message.direction}`}>
                    <div className="message-header">
                      <Text strong>
                        {message.subject || "Kein Betreff"}
                      </Text>
                      {isLongMessage && (
                        <Button
                          size="small"
                          type="text"
                          icon={isExpanded ? <UpOutlined /> : <DownOutlined />}
                          onClick={() => toggleMessageExpansion(message.id)}
                        />
                      )}
                    </div>

                    <Paragraph className="message-body" style={{ marginBottom: 0 }}>
                      {isLongMessage && !isExpanded
                        ? message.body.substring(0, 300) + "..."
                        : message.body}
                    </Paragraph>

                    {message.attachments && message.attachments.length > 0 && (
                      <div className="message-attachments">
                        <Space>
                          <PaperClipOutlined />
                          <Text>{message.attachments.length} Anhang/Anhänge</Text>
                        </Space>
                      </div>
                    )}

                    <div className="message-footer">
                      <Text className="message-time">
                        {formatMessageTime(message.received_at)}
                      </Text>
                      {message.direction === "outgoing" && (
                        <Tooltip title={message.is_read ? "Gelesen" : "Gesendet"}>
                          <span className="message-status">
                            {message.is_read ? (
                              <CheckCircleOutlined style={{ color: "#52c41a" }} />
                            ) : (
                              <CheckOutlined style={{ color: "#8c8c8c" }} />
                            )}
                          </span>
                        </Tooltip>
                      )}
                    </div>
                  </div>
                </div>
              </React.Fragment>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>
    </div>
  );
}; 